/**
 * «Звʼязані документи» (ч.639, рішення власника 2026-09-01) — кнопка в
 * інфо-рядку DocumentToolbar + попап з ланцюгом звʼязаних документів
 * (рахунок → реалізація → ТТН → ПН → повернення тощо).
 * Дані — GET /documents/related (реєстр link-колонок, RLS-безпечно).
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { DOC_ROUTES as SHARED_DOC_ROUTES } from '@/shared/utils/docRoutes';
import { useConfirm } from '@/components/ConfirmProvider';
import BodyPortal from './BodyPortal';
import { LinkDocumentButton, useLinkContext, useUnlinkDocument } from './DocLinks';
import type { DtKtDocRef } from './DtKtJournal';
import TchTable from '@/shared/components/TchTable';

interface RelatedDoc {
  document_type: string;
  document_id: string;
  relation: 'BASE' | 'CHILD' | 'CONTRACT';
  number: string | null;
  date: string | null;
  posted: boolean | null;
  /** ч.856: ORDER | PROFORMA_INVOICE — замовлення чи рахунок (одна таблиця). */
  order_kind?: string | null;
  /** ч.937: сума документа (перелік документів договору). */
  amount?: string | null;
  /** ч.1219: звʼязок із таблиці звʼязків — рядок і його походження: `manual` (привʼязано
   *  користувачем, ч.1220) або реквізит 1С (deal / basis / settlement / batch / order). */
  link_id?: string | null;
  link_kind?: string | null;
}

/** ч.1220: підпис звʼязку. Ручний — без напрямку: користувач просто поставив документи поруч. */
export function relationLabel(r: Pick<RelatedDoc, 'relation' | 'link_kind'>): string {
  if (r.link_kind === 'manual') return 'привʼязано';
  return r.relation === 'BASE' ? 'підстава' : 'на підставі';
}

/** «таблиця:id» звʼязаних документів — щоб не пропонувати привʼязати вже звʼязане. */
export function relatedKeys(items: RelatedDoc[] | undefined): ReadonlySet<string> {
  return new Set((items ?? []).map((r) => `${r.document_type}:${r.document_id}`));
}

/** Маршрути редакторів за document_type (для переходу з попапа). */
const DOC_ROUTES: Record<string, (id: string) => string> = {
  'doc.customer_orders': (id) => `/customer-orders/${id}`,
  'doc.supplier_orders': (id) => `/supplier-orders/${id}`,
  'doc.goods_sales': (id) => `/goods-sales/${id}`,
  'doc.goods_receipts': (id) => `/goods-receipts/${id}`,
  'doc.goods_returns_from_customer': (id) => `/goods-returns-from-customer/${id}`,
  'doc.goods_returns_to_supplier': (id) => `/goods-returns-to-supplier/${id}`,
  'doc.tnn': (id) => `/tnn/${id}`,
  'tax.tax_invoices_outgoing': (id) => `/tax-invoices-outgoing/${id}`,
  'tax.tax_invoices_incoming': (id) => `/tax-invoices-incoming/${id}`,
  'doc.bank_receipts': (id) => `/bank-receipts/${id}`,
  'doc.bank_payments': (id) => `/bank-payments/${id}`,
  'doc.cash_receipts': (id) => `/cash-receipts/${id}`,
  'doc.cash_payments': (id) => `/cash-payments/${id}`,
  'doc.currency_purchase_sale': (id) => `/banking/currency-operations/${id}`,
  'doc.service_acts': (id) => `/service-acts/${id}`,
  'doc.fixed_asset_inventory': (id) => `/assets/inventory/${id}`,
  'doc.asset_write_off': (id) => `/assets/write-off/${id}`,
  'doc.fixed_asset_sale': (id) => `/assets/sale/${id}`,
  'doc.vacation_orders': (id) => `/hr/vacation-orders/${id}`,
  'doc.vacation_calculations': (id) => `/hr/vacation-calculations/${id}`,
  'doc.dismissal_orders': (id) => `/dismissal-orders/${id}`,
  'doc.absence_orders': (id) => `/absence-orders/${id}`,
  'doc.retail_sales': (id) => `/retail/retail-sales/${id}`,
  'doc.retail_returns': (id) => `/retail/retail-returns/${id}`,
};

const EXTRA_NAMES: Record<string, string> = {
  'doc.tnn': 'Товарно-транспортні накладні',
  'tax.tax_invoices_outgoing': 'Податкові накладні (видані)',
  'tax.tax_invoices_incoming': 'Податкові накладні (вхідні)',
  'doc.warehouse_orders': 'Складські ордери',
  'doc.fixed_asset_inventory': 'Інвентаризація НА',
  'doc.asset_write_off': 'Списання НА',
  'doc.fixed_asset_sale': 'Продаж ОЗ',
  'doc.vacation_orders': 'Накази на відпустку',
  'doc.vacation_calculations': 'Розрахунки відпускних',
  'doc.vacation_compensations': 'Компенсації відпустки',
  'doc.dismissal_orders': 'Накази на звільнення',
  'doc.absence_orders': 'Накази про тривалу відсутність',
  'doc.tax_invoice_appendix_1': 'Додаток 1 до ПН',
  'doc.tax_invoice_appendix_2': 'Додаток 2 до ПН',
  'doc.advance_payment_tax_registration': 'ПН на аванс',
  'doc.tare_return': 'Повернення тари',
  'doc.currency_purchase_sale': 'Купівля-продаж валюти',
  'doc.tax_purpose_adjustments_tzr': 'Коригування призначення ТЗР',
};

const typeName = (t: string) => DOC_TABLE_NAMES_UK[t] ?? EXTRA_NAMES[t] ?? t;

/** ч.856: назва з урахуванням виду (рахунок ≠ замовлення — одна таблиця). */
export function relatedDocName(r: { document_type: string; order_kind?: string | null }): string {
  if (r.order_kind === 'PROFORMA_INVOICE') {
    return r.document_type === 'doc.customer_orders'
      ? 'Рахунок покупцю' : 'Рахунок від постачальника';
  }
  if (r.order_kind === 'ORDER') {
    return r.document_type === 'doc.customer_orders'
      ? 'Замовлення покупця' : 'Замовлення постачальнику';
  }
  return typeName(r.document_type);
}

/** ч.856: маршрут з урахуванням виду (рахунок → /customer-invoices). */
export function relatedDocRoute(r: {
  document_type: string; document_id: string; order_kind?: string | null;
}): string | undefined {
  if (r.order_kind === 'PROFORMA_INVOICE') {
    if (r.document_type === 'doc.customer_orders') return `/customer-invoices/${r.document_id}`;
    if (r.document_type === 'doc.supplier_orders') return `/supplier-invoices/${r.document_id}`;
  }
  // ч.1217: типів більше, ніж у локальній карті (ВМД, додаткові витрати, довіреності, акти
  // виробничих послуг) — решту беремо із загальної карти маршрутів документів.
  return (DOC_ROUTES[r.document_type] ?? SHARED_DOC_ROUTES[r.document_type])?.(r.document_id);
}
const fmtD = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('uk-UA');
};

/** ч.849: дані звʼязаних для правої колонки (2 останні + «ще»). */
export function useRelatedDocs(doc: DtKtDocRef, enabled = true) {
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  return useQuery({
    queryKey: ['related-docs', doc.type, docId],
    queryFn: () => api.get<RelatedDoc[]>('/documents/related', {
      params: { document_type: doc.type, document_id: docId },
    }).then((r) => r.data),
    enabled: enabled && !!docId,
  });
}

/** ч.937: УСІ документи договору (картка договору) — плоский перелік. */
export function useContractDocs(contractId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['contract-docs', contractId],
    queryFn: () => api.get<RelatedDoc[]>('/documents/by-contract', {
      params: { contract_id: contractId },
    }).then((r) => r.data),
    enabled: enabled && !!contractId,
  });
}

/** ч.849: маршрут редактора за типом документа (для правої колонки). */
export function docRoute(type: string, id: string): string | undefined {
  return (DOC_ROUTES[type] ?? SHARED_DOC_ROUTES[type])?.(id);
}

export { typeName as relatedTypeName };
export type { RelatedDoc };

/** Попап зі звʼязаними документами — окремо від кнопки (ч.1217): його відкривають і кнопка
 *  «звʼязані» редактора, і клік по рядку зведеного журналу «Комерції».
 *  `subtitle` — про який документ ідеться; `actions` — кнопки в шапці (напр. «відкрити документ»). */
export function RelatedDocsDialog({ doc, onClose, subtitle, actions }: {
  doc: DtKtDocRef; onClose: () => void; subtitle?: ReactNode; actions?: ReactNode;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  const q = useRelatedDocs(doc, !!docId);
  const ctx = useLinkContext(doc, !!docId);
  const unlink = useUnlinkDocument();
  if (!docId) return null;
  const canUnlink = !!ctx.data?.can_link;
  const hasManual = (q.data ?? []).some((r) => r.link_kind === 'manual');
  return (
    <BodyPortal>
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100,
      background: 'var(--overlay)', display: 'flex',
      alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" role="dialog" aria-label="Звʼязані документи" style={{ width: 720, maxWidth: '94vw', maxHeight: '75vh',
        overflowY: 'auto', margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem', gap: '0.4rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Звʼязані документи</h3>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
            <LinkDocumentButton doc={doc} linkedKeys={relatedKeys(q.data)} />
            {actions}
            <button onClick={onClose}>закрити</button>
          </div>
        </div>
        {subtitle && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{subtitle}</div>}
        {q.isLoading && <div style={{ color: 'var(--muted)' }}>Завантаження…</div>}
        {q.isError && <div style={{ color: 'var(--badge-red-fg)' }}>Не вдалося завантажити звʼязані документи.</div>}
        {unlink.isError && (
          <div role="alert" style={{ color: 'var(--badge-red-fg)' }}>Не вдалося прибрати звʼязок.</div>
        )}
        {q.data && q.data.length === 0 && (
          <div style={{ color: 'var(--muted)' }}>Звʼязаних документів немає.</div>
        )}
        {q.data && q.data.length > 0 && (
          <TchTable>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Документ</th>
                <th style={{ width: 140 }}>№</th>
                <th style={{ width: 100 }}>Дата</th>
                <th style={{ width: 90 }}>Проведено</th>
                <th style={{ width: 90 }}>Звʼязок</th>
                {hasManual && <th style={{ width: 90 }} />}
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => {
                const route = relatedDocRoute(r);
                return (
                  <tr key={`${r.document_type}:${r.document_id}`}
                    style={route ? { cursor: 'pointer' } : undefined}
                    title={route ? 'Відкрити документ' : undefined}
                    onClick={() => { if (route) { onClose(); navigate(route); } }}
                    onMouseEnter={(e) => { if (route) e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{route
                      ? <span style={{ color: 'var(--primary)' }}>{relatedDocName(r)}</span>
                      : relatedDocName(r)}</td>
                    <td>{r.number ?? '—'}</td>
                    <td>{fmtD(r.date)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {r.posted == null ? '' : r.posted
                        ? <span style={{ color: 'var(--badge-green-fg)' }}>✓</span>
                        : <span style={{ color: 'var(--text-secondary)' }}>ні</span>}
                    </td>
                    <td style={{ color: 'var(--muted)', fontSize: '0.78rem' }}
                      title={r.link_kind && r.link_kind !== 'manual' ? 'Звʼязок перенесено з 1С' : undefined}>
                      {relationLabel(r)}
                    </td>
                    {hasManual && (
                      <td style={{ textAlign: 'center' }}>
                        {r.link_kind === 'manual' && r.link_id && (
                          <button type="button" disabled={!canUnlink || unlink.isPending}
                            title={canUnlink ? 'Прибрати цей звʼязок (документи не змінюються)'
                              : 'Недостатньо прав, щоб змінювати цей документ'}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const ok = await confirm({
                                message: `Прибрати звʼязок із документом «${relatedDocName(r)} № ${r.number ?? '—'}»? Самі документи не змінюються.`,
                                okLabel: 'прибрати',
                              });
                              if (ok && r.link_id) unlink.mutate(r.link_id);
                            }}>
                            відвʼязати
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
        )}
      </div>
    </div>
    </BodyPortal>
  );
}

export function RelatedDocsButton({ doc }: { doc: DtKtDocRef }) {
  const [open, setOpen] = useState(false);
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={!docId}
        title={docId
          ? 'Ланцюг звʼязаних документів (рахунок → реалізація → ТТН → ПН → повернення…)'
          : 'Спершу збережіть документ'}>
        звʼязані
      </button>
      {open && docId && <RelatedDocsDialog doc={doc} onClose={() => setOpen(false)} />}
    </>
  );
}
